import "./orderSummary.scss";

import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { AlertBanner } from "@runwayhealth/runway-components-react";
import * as casesActions from "@store/cases/casesActions";
import { Case, DeclinedInvoiceMetadata, InvoiceLineItem } from "@types-runway/case";
import { formatCentsAsDollars } from "@utils/billing/formatAmount";

interface SummaryProps {
  case_: Case;
}

interface NoticeProps {
  variant: "info" | "warning";
  heading: string;
  children: string;
}

const Notice = ({ variant, heading, children }: NoticeProps) => (
  <div className="summary-notice" role="status">
    <AlertBanner variant={variant} isRounded>
      <AlertBanner.Message>
        <strong>{heading}</strong>
        <p className="summary-notice-body">{children}</p>
      </AlertBanner.Message>
    </AlertBanner>
  </div>
);

const DeclineNotice = ({ metadata }: { metadata?: DeclinedInvoiceMetadata }) => (
  <Notice variant="warning" heading="Your last payment attempt was declined.">
    {metadata?.message || "Please check your payment method."}
  </Notice>
);

const UnavailableSummary = () => (
  <div className="summary summary-unavailable" role="status">
    <p className="summary-unavailable-title">We can't show the amount for this order yet.</p>
    <p className="summary-unavailable-body">
      Your payment didn't go through, so your order is still on hold.
    </p>
  </div>
);

const OrderSummary = ({ case_ }: SummaryProps) => {
  const dispatch = useDispatch();
  const { invoice, isInvoiceUnavailable } = case_.CurexaOrder;
  const isDeclinedSnapshot = invoice?.metadata?.isEstimate === true;
  const invoiceItems = (invoice?.lineItems ?? []).map((item: InvoiceLineItem) => {
    return {
      ...item,
      amount: formatCentsAsDollars(item.amount ?? 0),
    };
  });
  const shippingCost = formatCentsAsDollars(invoice?.shippingAmount ?? 0);
  const invoiceTotal = formatCentsAsDollars(invoice?.amount ?? 0);

  useEffect(() => {
    if (!invoice && !isInvoiceUnavailable) {
      dispatch({
        type: casesActions.REQUEST_ORDER_INVOICE,
        orderId: case_.CurexaOrder.id,
        caseId: case_.id,
      });
    }
  }, [case_.CurexaOrder.id, invoice, isInvoiceUnavailable]);

  if (isInvoiceUnavailable && !invoice) return <UnavailableSummary />;

  return (
    <div className="summary">
      {isDeclinedSnapshot && <DeclineNotice metadata={invoice?.metadata} />}
      <div className="summary-table">
        <div className="row">
          <div className="col1 title">Item</div>
          <div className="col2 title">Amount</div>
        </div>
        {invoiceItems.map((item, i) => (
          <div key={i} className="row">
            <div className="col1">{item.description}</div>
            <div className="col2">{item.amount}</div>
          </div>
        ))}
        <div className="row">
          <div className="col1">Shipping</div>
          <div className="col2">{shippingCost}</div>
        </div>
        <div className="row total-amount">
          <div className="col1">
            <b>{isDeclinedSnapshot ? "Amount from your last attempt" : "Order Total"}</b>
          </div>
          <div className="col2">
            <b>{invoiceTotal}</b>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
